import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { TabPanel, TabView } from 'primereact/tabview';
import React from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { withTranslation } from 'react-i18next';
import BaseListContainer from '../baseContainers/BaseListContainer';
import ActionButton from '../components/ActionButton';
import ActionLink from '../components/ActionLink';
import { CustomDialog } from '../components/CustomDialog';
import { CustomMessages } from '../components/CustomMessages';
import DivContainer from '../components/DivContainer';
import BlockUi from '../components/waitPanel/BlockUi';
import { baseReportCsv, baseReportPdf, baseXLSXReport } from '../report/ReportUtils';
import SettingService from '../services/SettingService';
import UserAgreementMapService from '../services/UserAgreementMapService';
import UserEditDialog from './UserEditDialog';

class SettingsContainer extends BaseListContainer {
    constructor(props) {
        super(props, new UserAgreementMapService());
        this.settingService = new SettingService();
        this.state = {
            activeIndex: 0,
            list: [],
            selectedList: [],
            selectAll: false,
            loading: true,
            size: 0,
            first: 0,
            criteria: this.getCleanSearchCriteria(),
            userEditDialogVisible: false,
            confirmationSaveDialogVisible: false,
            permissionOptions: [],
            telephoneCodeOptions: [],
            element: {
                username: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                phoneNumberCode: undefined,
                phoneNumberValue: '',
                email: '',
                userType: 'STANDARD',
                active: true,
                permissions: []
            },
            allList: {
                content: [],
                totalElements: 0
            },
            activeList: {
                content: [],
                totalElements: 0
            },
            blockedList: {
                content: [],
                totalElements: 0
            }
        };
        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleOnSelectAllChange = this.handleOnSelectAllChange.bind(this);
        this.handleOpenNewDialog = this.handleOpenNewDialog.bind(this);
        this.handleOpenDialog = this.handleOpenDialog.bind(this);
        this.handleCloseDialog = this.handleCloseDialog.bind(this);
    }

    getCleanSearchCriteria() {
        return {
            firstResult: 0,
            maxResults: 10,
            sortField: 'username',
            sortAsc: false
        };
    }

    updateSearchCriteria(criteria) {
        return {
            firstResult: criteria.firstResult,
            maxResults: criteria.maxResults,
            sortField: criteria.sortField,
            sortAsc: criteria.sortAsc
        };
    }

    componentDidMount() {
        super.componentDidMount();
        const { t } = this.props;
        if (this.defaultLoading) {
            this.setState({ loading: true });
        }
        this.service
            .getPermissionOpt('permissions', t)
            .then((permissionOptions) =>
                this.setState({
                    permissionOptions
                })
            )
            .catch(() => {
                this.showErrorMessage(t('error.getPermissionsError'));
            });
        this.settingService
            .getTelephoneCodeList()
            .then((telephoneCodeOptions) =>
                this.setState({
                    telephoneCodeOptions
                })
            )
            .catch(() => {
                this.showErrorMessage(t('error.getTelephoneCodesError'));
            });
    }

    getContainerListName() {
        return 'settings-container';
    }

    getListTitle() {
        return 'settings.users.listHeader';
    }

    translate(key) {
        const { t } = this.props;
        return t(key);
    }

    showConfirmationSave() {
        this.setState({ confirmationSaveDialogVisible: true });
    }

    hideConfirmationSave() {
        this.setState({ confirmationSaveDialogVisible: false });
    }

    prepareHeaderItems() {
        return [
            { type: 'LABEL', className: 'float-left' },
            {
                type: 'BUTTON',
                className: 'right-side',
                variant: 'white',
                disabled: !this.state.selectedList || this.state.selectedList.length < 1,
                customRenderFunction: this.renderPrintButton
            }
        ];
    }

    prepareColumns() {
        const { t } = this.props;
        return [
            {
                field: 'username',
                header: t('settings.users.columns.username'),
                sortable: true,
                sortField: 'username'
            },
            {
                field: 'active',
                header: t('settings.users.columns.active'),
                sortable: true,
                sortField: 'active',
                body: this.activeInUiTemplate.bind(this)
            },
            {
                field: 'fullName',
                header: t('settings.users.columns.fullName'),
                sortable: true,
                sortField: 'firstName',
                body: this.fullNameTemplate.bind(this)
            },
            {
                field: 'phoneNumber',
                header: t('settings.users.columns.phoneNumber'),
                sortable: true,
                sortField: 'phoneNumber'
            },
            {
                field: 'email',
                header: t('settings.users.columns.email'),
                sortable: true,
                sortField: 'email'
            },
            {
                key: 'actions',
                header: t('list.actions'),
                body: this.actionTemplate.bind(this),
                className: 'table-column-action'
            },
            {
                field: 'userType',
                header: t('settings.users.columns.userType'),
                sortable: true,
                sortField: 'userType',
                body: this.userTypeTemplate.bind(this)
            }
        ];
    }

    activeInUiTemplate(rowData) {
        if (rowData && rowData?.active !== null && rowData?.active !== undefined && rowData?.active === true) {
            return (
                <div style={{ marginLeft: '15px' }}>
                    <Checkbox disabled={true} checked={true}></Checkbox>
                </div>
            );
        } else
            return (
                <div style={{ marginLeft: '15px' }}>
                    <Checkbox disabled={true} checked={false}></Checkbox>
                </div>
            );
    }

    activeInReportTemplate(rowData) {
        return rowData?.active === true ? 'TAK' : 'NIE'
    }

    fullNameTemplate(rowData) {
        return `${rowData?.firstName || ''} ${rowData?.lastName || ''}`.trim();
    }

    actionTemplate(rowData) {
        const { t } = this.props;
        const isRowMainUser = rowData?.userType === 'MAIN';
        const hasEditSettingsPermission = this.isUserHasPermission('PERMISSION_EDIT_SETTINGS') &&
            !(this.authService.getProfile().helpdeskUserRole === 'REGIONAL_MANAGER' || this.authService.getProfile().helpdeskUserRole === 'SALES_REPRESENTATIVE');
        const isActiveLink = !isRowMainUser && hasEditSettingsPermission;

        return (
            <ActionLink
                label={t('details.manageButton')}
                handleClick={this.handleOpenDialog.bind(this, rowData)}
                variant='blue'
                className='edit-blue-link'
                key={'view-button'}
                disabled={!isActiveLink}
                rendered={!isRowMainUser}
            />
        );
    }

    userTypeTemplate(rowData) {
        const { t } = this.props;
        if (rowData && rowData?.userType !== null && rowData?.userType !== undefined && rowData?.userType === 'MAIN') {
            return t('settings.user.MAIN');
        } else if (rowData && rowData?.userType !== null && rowData?.userType !== undefined && rowData?.userType === 'STANDARD') {
            return t('settings.user.STANDARD')
        } else {
            return '';
        }
    }

    render() {
        const { t } = this.props;
        const { blocking, loading } = this.state;
        return (
            <React.Fragment>
                <DivContainer colClass='container-fluid settings-container'>
                    <BlockUi tag='div' blocking={blocking || loading} loader={this.loader}>
                        {this.renderDetails()}
                        <BreadcrumbsItem to='/settings'>{t('invoice.listName')}</BreadcrumbsItem>
                        {this.renderView()}
                        {this.renderAddToTrustedDevicesDialog()}
                        {this.state.userEditDialogVisible && (
                            <UserEditDialog
                                key={`user-dialog-${this.state.element?.id}`}
                                element={this.state.element}
                                visible={this.state.userEditDialogVisible}
                                viewMode={this.state.dialogViewMode}
                                permissionOptions={this.state.permissionOptions}
                                telephoneCodeOptions={this.state.telephoneCodeOptions}
                                afterSave={(e) => {
                                    this.handleCloseDialog(e, () => {
                                        this.readMessage();
                                        this.refreshTable();
                                        this.unblockUi();
                                    });
                                }}
                                afterSaveBeforeConfirmation={(e) => {
                                    this.handleCloseDialog(e, () => {
                                        this.readMessage();
                                        this.refreshTable();
                                        this.showConfirmationSave();
                                        this.unblockUi();
                                    });
                                }}
                                afterError={() => {
                                    this.readMessage();
                                    this.unblockUi();
                                }}
                                onHide={(e) => {
                                    this.handleCloseDialog(e);
                                    this.refreshTable();
                                }}
                                blockUi={this.blockUi}
                                unblockUi={this.unblockUi}
                                _isMounted={this._isMounted}
                            />
                        )}
                        <CustomDialog
                            viewMode={this.props.viewMode}
                            visible={this.state.confirmationSaveDialogVisible}
                            name='confirmationSaveDialog'
                            className='confirmation-save-user-dialog'
                            onHide={() => this.hideConfirmationSave()}
                            header={t('settings.user.updateConfirmationHeader')}
                        >
                            <DivContainer>
                                {t('settings.user.updateConfirmationDescription')}
                            </DivContainer>
                        </CustomDialog>
                    </BlockUi>
                </DivContainer>
            </React.Fragment>
        );
    }

    renderDetails() {
        const { t } = this.props;
        return (
            <div>
                <div className='row add-user-row'>
                    <div className='col-width-auto'>
                        <ActionButton
                            label={t('settings.addUser')}
                            handleClick={(e) => this.handleOpenNewDialog(e)}
                            variant='blue'
                            disabled={!this.isUserHasPermission('PERMISSION_EDIT_SETTINGS') || this.authService.getProfile().helpdeskUserRole === 'SALES_REPRESENTATIVE' ||
                                this.authService.getProfile().helpdeskUserRole === 'REGIONAL_MANAGER'}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderView() {
        const { t } = this.props;
        return (
            <DivContainer>
                <CustomMessages id='custom-messages' ref={(el) => (this.messages = el)} rendered={!this.props.embedded} />
                <BlockUi
                    tag='div'
                    blocking={this.state.blocking || this.state.loading}
                    loader={this.loader}
                    rendered={!this.props.embedded}
                >
                    <Card className='list-card card-list-container'>
                        <DivContainer colClass='row'>{this.renderHeader()}</DivContainer>
                        <TabView activeIndex={this.state.activeIndex} onTabChange={this.handleTabChange} className='sub-tabview'>
                            <TabPanel header={`${t('settings.users.all')} (${this.state.allList?.totalElements})`}>
                                {this.renderDataTable(
                                    this.prepareColumns(),
                                    this.state.allList?.content,
                                    this.state.allList?.totalElements
                                )}
                            </TabPanel>
                            <TabPanel header={`${t('settings.users.active')} (${this.state.activeList?.totalElements})`}>
                                {this.renderDataTable(
                                    this.prepareColumns(),
                                    this.state.activeList?.content,
                                    this.state.activeList?.totalElements
                                )}
                            </TabPanel>
                            <TabPanel header={`${t('settings.users.blocked')} (${this.state.blockedList?.totalElements})`}>
                                {this.renderDataTable(
                                    this.prepareColumns(),
                                    this.state.blockedList?.content,
                                    this.state.blockedList?.totalElements
                                )}
                            </TabPanel>
                        </TabView>
                    </Card>
                </BlockUi>
            </DivContainer>
        );
    }

    getList() {
        const { criteria } = this.state;
        this.service
            .getList(criteria)
            .then((res) => {
                if (this._isMounted) {
                    this.setState(
                        {
                            allList: {
                                content: this.updateListElements(res.allList?.content),
                                totalElements: res.allList?.totalElements
                            },
                            activeList: {
                                content: this.updateListElements(res.activeList?.content),
                                totalElements: res.activeList?.totalElements,
                            },
                            blockedList: {
                                content: this.updateListElements(res.blockedList?.content),
                                totalElements: res.blockedList?.totalElements,
                            },
                            loading: false,
                            list: this.updateListElements(res.allList?.content),
                            size: res.allList?.totalElements
                        },
                        () => this.onAfterGetList(res)
                    );
                }
            })
            .catch(() => {
                if (this._isMounted) {
                    this.setState(
                        {
                            allList: { content: [], totalElements: 0 },
                            activeList: { content: [], totalElements: 0 },
                            blockedList: { content: [], totalElements: 0 },
                            loading: false
                        },
                        () => {
                            this.afterGetListError();
                        }
                    );
                }
            });
    }

    onAfterGetList(res) {
        if (this.props.onAfterGetList) {
            this.props.onAfterGetList(res);
        }
        this.unblockUi();
    }

    handleTabChange(e) {
        this.setState(
            {
                activeIndex: e.index,
                selectAll: false,
                selectedList: []
            },
            () => {
                this.saveCookie(`user-list-activeIndex`, this.state.activeIndex);
            }
        );
    }

    handleOnSelectAllChange(e) {
        const selectAll = e.checked;
        if (selectAll) {
            if (this.props.lazy) {
                let allCriteria = { ...this.state.criteria };
                allCriteria.firstResult = 0;
                allCriteria.maxResults = 2147483647;
                this.setState({ loading: true });
                this.service
                    .getList(allCriteria)
                    .then((data) => {
                        if (this._isMounted) {
                            this.setState({
                                selectAll: true,
                                selectedList: this.getContentFromResponse(data),
                                loading: false,
                            });
                        }
                    })
                    .catch((err) => {
                        this.setState({ loading: false });
                    });
            } else {
                if (this.state.activeIndex >= 0) {
                    switch (this.state.activeIndex) {
                        case 0:
                            this.setState({
                                selectAll: true,
                                selectedList: this.state.allList.content
                            });
                            break;
                        case 1:
                            this.setState({
                                selectAll: true,
                                selectedList: this.state.activeList.content
                            });
                            break;
                        case 2:
                            this.setState({
                                selectAll: true,
                                selectedList: this.state.blockedList.content
                            });
                            break;
                        default:
                            this.setState({
                                selectAll: true,
                                selectedList: this.state.list
                            });
                    }
                }
            }
        } else {
            this.setState({
                selectAll: false,
                selectedList: [],
                loading: false,
            });
        }
    }

    getContentFromResponse(response) {
        if (this.state.activeIndex >= 0) {
            switch (this.state.activeIndex) {
                case 0:
                    return response.allList.content;
                case 1:
                    return response.activeList.content;
                case 2:
                    return response.blockedList.content;
                default:
                    return [];
            }
        }
        return [];
    }

    getTotalElements() {
        if (this.state.activeIndex >= 0) {
            switch (this.state.activeIndex) {
                case 0:
                    return this.state.allList.totalElements;
                case 1:
                    return this.state.activeList.totalElements;
                case 2:
                    return this.state.blockedList.totalElements;
                default:
                    return [];
            }
        }
        return [];
    }

    handleOpenNewDialog(e) {
        let clearElement = this.state.element;

        if (e) {
            e.preventDefault();
        }
        this.setState({ userEditDialogVisible: true, element: clearElement, dialogViewMode: 'NEW' });
    }

    handleOpenDialog(rowData, e) {
        if (e) {
            e.preventDefault();
        }
        this.setState({ userEditDialogVisible: true, element: rowData, dialogViewMode: 'EDIT' });
    }

    handleCloseDialog(e, callback) {
        let clearElement = {
            username: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            phoneNumberCode: undefined,
            phoneNumberValue: '',
            email: '',
            userType: 'STANDARD',
            active: true,
            permissions: []
        };

        if (e) {
            e.preventDefault();
        }
        this.setState({ userEditDialogVisible: false, element: clearElement }, () => {
            if (callback) {
                callback();
            }
        });
    }

    generateReport(format) {
        const { t } = this.props;
        const list = this.state.selectedList;
        const columns = [
            { header: t('settings.users.columns.username'), field: 'username' },
            { header: t('settings.users.columns.active'), field: 'active', getColFunction: this.activeInReportTemplate.bind(this) },
            { header: t('settings.users.columns.fullName'), field: 'fullName', getColFunction: this.fullNameTemplate.bind(this) },
            { header: t('settings.users.columns.phoneNumber'), field: 'phoneNumber' },
            { header: t('settings.users.columns.email'), field: 'email' },
            { header: t('settings.users.columns.userType'), field: 'userType', getColFunction: this.userTypeTemplate.bind(this) }
        ];

        if (format === 'pdf') {
            const blob = baseReportPdf({ list, title: t('settings.users.listHeader'), columns, landscape: true }, this.props.t);
            return Promise.resolve(blob);
        } else if (format === 'csv') {
            const fields = [
                { value: 'username', label: t('settings.users.columns.username') },
                { value: this.activeInReportTemplate.bind(this), label: t('settings.users.columns.active') },
                { value: this.fullNameTemplate.bind(this), label: t('settings.users.columns.fullName') },
                { value: 'phoneNumber', label: t('settings.users.columns.phoneNumber') },
                { value: 'email', label: t('settings.users.columns.email') },
                { value: this.userTypeTemplate.bind(this), label: t('settings.users.columns.userType') }
            ];
            return Promise.resolve(baseReportCsv(list, fields));
        } else if (format === 'xlsx' || format === 'ods') {
            const request = {};
            request.columns = columns;
            request.list = list;
            request.sheetName = t(this.getListTitle()).replaceAll(/[*?\][\\/]/g, ' ,');
            return baseXLSXReport(request, format);
        }
    }
}

SettingsContainer.defaultProps = {
    lazy: false,
    disableSelection: false,
    selectionMode: 'multiple'
};

SettingsContainer.propTypes = {};

export default withTranslation()(SettingsContainer);
